import { useEffect, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

// Defaults to the current signed-in user, editable — suggestions cover
// anyone in the catalog, since maintainers can be any ska-telescope user.
export const MaintainersPicker = ({
  onChange,
  rawErrors,
  formData,
}: FieldExtensionComponentProps<string[]>) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaulted, setDefaulted] = useState(false);
  const [loadError, setLoadError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [{ items: users }, identity] = await Promise.all([
          catalogApi.getEntities({
            filter: { kind: 'User' },
            fields: ['metadata.name'],
          }),
          identityApi.getBackstageIdentity(),
        ]);
        if (cancelled) return;

        setOptions(users.map(u => u.metadata.name));

        if (!defaulted) {
          const currentUsername = identity.userEntityRef.split('/').pop();
          if (currentUsername) {
            onChange([currentUsername]);
          }
          setDefaulted(true);
        }
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to load maintainers from the catalog', error);
          setLoadError(
            'Failed to load maintainers from the catalog. Enter usernames manually.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // Only default once, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      multiple
      freeSolo={Boolean(loadError)}
      loading={loading}
      options={options}
      value={formData ?? []}
      onChange={(_, value) => onChange(value)}
      renderInput={params => (
        <TextField
          {...params}
          label="Maintainers"
          helperText={
            loadError ?? 'Defaults to you. Add up to 2 maintainers in total.'
          }
          error={Boolean(loadError) || rawErrors?.length > 0}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export const MaintainersPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'MaintainersPicker',
    component: MaintainersPicker,
  }),
);
